/// <amd-module name="Repos/Changes/Components/ChangesExplorerView" />
import * as React from "react";
import * as Resources from "Repos/Changes/Resources";
import { ChangesContext, IChangesContext } from "Repos/ChangesCommon/ChangesContext";
import { IChange, IChangeCollection } from "Repos/ChangesCommon/Contracts";
import {
    IChangesTreeDiscussionIntegration,
    IDiffSummaryDiscussionIntegration,
    IDiscussionIntegrationService
} from "Repos/Discussion/Common/DiscussionContracts";
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableValue, useObservable } from "VSS/Core/Observable";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { IEditorWidgetRegistration } from "VSS/Features/Monaco/Contracts";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { css, useComponentContext } from "VSS/Platform/Layout";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { IFilterProps } from "VSSUI/Filter";
import { TitleSize } from "VSSUI/Header";
import { Link } from "VSSUI/Link";
import { IMenuItem } from "VSSUI/Menu";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { CustomPanel, PanelCloseButtonSize, PanelHeader } from "VSSUI/Panel";
import { Splitter, SplitterElementPosition } from "VSSUI/Splitter";
import { ScreenSizeConditional } from "VSSUI/Utilities/ScreenSize";
import { ITreeItem } from "VSSUI/Utilities/TreeItemProvider";
import { ChangedFileViewMode, IChangeWithDiscussionThread } from "../Contracts";
import { ChangeCompareHeader } from "./ChangeCompareHeader";
import { ChangesExplorerTree } from "./ChangesExplorerTree";
import { ChangesViewer } from "./ChangesViewer";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

const splitterSettingsKey = "Repos.ChangeExplorerView.SplitterWidth";

export interface IChangesExplorerViewProps<TVersionDescriptor> {
    addDiffKeyboardShortcuts: boolean;
    changes: IChangeCollection<TVersionDescriptor>;
    discussionService?: IDiscussionIntegrationService;
    filterProps?: IFilterProps;
    getFileUrl?: (path: string) => string;
    getHighlightText?: () => string | undefined;
    getItemMenuItems?: (
        item: IChange,
        treeDiffIntegration?: IChangesTreeDiscussionIntegration<ITreeItem<IChangeWithDiscussionThread>>,
        rootDiffIntegration?: IDiffSummaryDiscussionIntegration
    ) => IReadonlyObservableArray<IMenuItem>;
    preFilterContent?: JSX.Element;
    selectedThreadId?: IReadonlyObservableValue<number | undefined>;
    viewerWidgets?: IReadonlyObservableArray<IEditorWidgetRegistration> | IEditorWidgetRegistration[];
    isMergeCommit?: boolean;
    commitTooManyChanges?: boolean;
    isConflictResolutionIteration?: boolean;
    conflictResolutionIterationLink?: string;
    isChangeset?: boolean;
    onCheckmarkChange?(tableRow: IChange, checkmarked: boolean): void;
    onViewChange?(): void;
}

export function ChangesExplorerView<TVersionDescriptor>(props: IChangesExplorerViewProps<TVersionDescriptor>) {
    const {
        addDiffKeyboardShortcuts,
        changes,
        discussionService,
        filterProps,
        getFileUrl,
        getHighlightText,
        getItemMenuItems,
        onCheckmarkChange,
        preFilterContent,
        selectedThreadId,
        viewerWidgets,
        isMergeCommit,
        commitTooManyChanges,
        isConflictResolutionIteration,
        conflictResolutionIterationLink,
        isChangeset,
        onViewChange
    } = props;

    const { pageContext } = useComponentContext();
    const [isTreeCollapsed, setTreeCollapsed] = React.useState(false);
    const [fileViewMode, setFileViewMode] = useObservable(ChangedFileViewMode.Diff);
    const fileView = new ObservableValue<boolean>(true);
    const [messageDismissed, setMessageDismissed] = useObservable(false);

    const [showFileTreePanel, setShowFileTreePanel] = useObservable<boolean>(false);

    const changeViewer = (smallScreen: boolean, changesContext: IChangesContext<TVersionDescriptor>) => {
        return (
            <ChangesViewer
                addDiffKeyboardShortcuts={addDiffKeyboardShortcuts}
                changes={changes}
                className={css("flex-grow", !smallScreen && "absolute-fill")}
                discussionService={discussionService}
                fileViewMode={fileViewMode}
                getFileUrl={getFileUrl}
                highlightText={getHighlightText && getHighlightText()}
                key={changes.key}
                onViewFile={(event, change) => {
                    if (!event.ctrlKey && !event.metaKey && (event as React.MouseEvent).button !== 1) {
                        changesContext.navigationService.setSelectedItem(change);
                        event.preventDefault();
                        if (onViewChange) 
                            onViewChange();
                    }
                }}
                selectedItem={changesContext.navigationService.getSelectedItem()}
                selectedThreadId={selectedThreadId}
                viewerWidgets={viewerWidgets}
                isMergeCommit={isMergeCommit}
                commitTooManyChanges={commitTooManyChanges}
                isConflictResolutionIteration={isConflictResolutionIteration}
                conflictResolutionIterationLink={conflictResolutionIterationLink}
                isChangeset={isChangeset}
            />
        );
    };

    const explorerTree = (smallScreen: boolean, changesContext: IChangesContext<TVersionDescriptor>) => {
        return (
            <ChangesExplorerTree
                className={css(!smallScreen && "absolute-fill")}
                changes={changes}
                discussionService={discussionService}
                getItemMenuItems={getItemMenuItems}
                key={changes.key}
                onActivate={(e, change) => {
                    fileView.value = true;
                    changesContext.navigationService.setSelectedItem(change);
                    setShowFileTreePanel(false);
                    if (onViewChange) 
                        onViewChange();
                }}
                onCheckmarkChange={onCheckmarkChange}
                highlightText={getHighlightText && getHighlightText()}
                selectedItem={changesContext.navigationService.getSelectedItem()}
                selectedThreadId={selectedThreadId}
                showLines={smallScreen}
            />
        );
    };

    if (isFeatureFlagEnabled(pageContext, "WebAccess.PullRequest.LoadMoreFiles")) {
        return (
            <ChangesContext.Consumer>
                {(changesContext: IChangesContext<TVersionDescriptor>) => (
                    <>
                        {changes.loadMoreChanges && (
                            <Observer messageDismissed={messageDismissed}>
                                {() => {
                                    return messageDismissed.value ? null : (
                                        <MessageCard
                                            className="margin-16"
                                            severity={MessageCardSeverity.Info}
                                            onDismiss={() => setMessageDismissed(true)}
                                        >
                                            <FormatComponent format={Resources.MoreChangesMessageFormat}>
                                                <span>{changes.totalChanges}</span>
                                                <Link onClick={() => changes.loadMoreChanges!()}>{Resources.ClickHere}</Link>
                                            </FormatComponent>
                                        </MessageCard>
                                    );
                                }}
                            </Observer>
                        )}
                        <Observer selectedItem={changesContext.navigationService.getSelectedItem()}>
                            {(selectionProps: { selectedItem: IChange }) => {
                                return (
                                    <ChangeCompareHeader
                                        changes={changes}
                                        discussionService={discussionService}
                                        fileViewMode={fileViewMode}
                                        filterProps={filterProps}
                                        onNavButtonClicked={() => setShowFileTreePanel(true)}
                                        pageContext={pageContext}
                                        preFilterContent={preFilterContent}
                                        setFileViewMode={setFileViewMode}
                                    />
                                );
                            }}
                        </Observer>
                        <Observer changes={changesContext.navigationService.getChanges()}>
                            {(selectionProps: { changes: IChangeCollection<TVersionDescriptor> | undefined}) => {
                                return (
                                <ScreenSizeConditional condition={screenSize => screenSize === ScreenSize.xsmall}>
                                    <ConditionalChildren renderChildren={showFileTreePanel}>
                                        <CustomPanel contentClassName="repos-changes-explorer-panel no-padding" onDismiss={() => setShowFileTreePanel(false)}>
                                            <PanelHeader
                                                className="repos-changes-explorer-panel-header"
                                                closeButtonSize={PanelCloseButtonSize.small}
                                                onDismiss={() => setShowFileTreePanel(false)}
                                                showSeparator={true}
                                                titleProps={{
                                                    size: TitleSize.Small,
                                                    text: Resources.ChangedFilesPanelTitle
                                                }}
                                            />
                                            {explorerTree(true, changesContext)}
                                        </CustomPanel>
                                    </ConditionalChildren>
                                    {changeViewer(true, changesContext)}
                                </ScreenSizeConditional>
                                );
                            }}
                        </Observer>
                        <Observer changes={changesContext.navigationService.getChanges()}>
                            {(selectionProps: { changes: IChangeCollection<TVersionDescriptor> | undefined}) => {
                                return (
                                <ScreenSizeConditional condition={screenSize => screenSize !== ScreenSize.xsmall}>
                                    <Splitter
                                        className="scroll-hidden bolt-page-grey repos-changes-explorer-splitter"
                                        collapsed={isTreeCollapsed}
                                        expandTooltip={Resources.ShowFilesTree}
                                        farElementClassName="flex-column relative"
                                        fixedElement={SplitterElementPosition.Near}
                                        initialFixedSize={parseInt("" + tryGetLocalStorageValue(splitterSettingsKey)) || 300}
                                        minFixedSize={180}
                                        nearElementClassName="flex-column relative"
                                        onFixedSizeChanged={newSize => {
                                            trySetLocalStorageValue(splitterSettingsKey, "" + newSize);
                                        }}
                                        onCollapsedChanged={setTreeCollapsed}
                                        onRenderFarElement={() => changeViewer(false, changesContext)}
                                        onRenderNearElement={() => explorerTree(false, changesContext)}
                                    />
                                </ScreenSizeConditional>
                                );
                            }}
                        </Observer>
                    </>
                )}
            </ChangesContext.Consumer>
        );
    }

    return (
        <ChangesContext.Consumer>
            {(changesContext: IChangesContext<TVersionDescriptor>) => (
                <>
                    {changes.loadMoreChanges && (
                        <Observer messageDismissed={messageDismissed}>
                            {() => {
                                return messageDismissed.value ? null : (
                                    <MessageCard
                                        className="margin-16"
                                        severity={MessageCardSeverity.Info}
                                        onDismiss={() => setMessageDismissed(true)}
                                    >
                                        <FormatComponent format={Resources.MoreChangesMessageFormat}>
                                            <span>{changes.totalChanges}</span>
                                            <Link onClick={() => changes.loadMoreChanges!()}>{Resources.ClickHere}</Link>
                                        </FormatComponent>
                                    </MessageCard>
                                );
                            }}
                        </Observer>
                    )}
                    <Observer selectedItem={changesContext.navigationService.getSelectedItem()}>
                        {(selectionProps: { selectedItem: IChange }) => {
                            return (
                                <ChangeCompareHeader
                                    changes={changes}
                                    discussionService={discussionService}
                                    fileViewMode={fileViewMode}
                                    filterProps={filterProps}
                                    onNavButtonClicked={() => setShowFileTreePanel(true)}
                                    pageContext={pageContext}
                                    preFilterContent={preFilterContent}
                                    setFileViewMode={setFileViewMode}
                                />
                            );
                        }}
                    </Observer>
                    <ScreenSizeConditional condition={screenSize => screenSize === ScreenSize.xsmall}>
                        <ConditionalChildren renderChildren={showFileTreePanel}>
                            <CustomPanel contentClassName="repos-changes-explorer-panel no-padding" onDismiss={() => setShowFileTreePanel(false)}>
                                <PanelHeader
                                    className="repos-changes-explorer-panel-header"
                                    closeButtonSize={PanelCloseButtonSize.small}
                                    onDismiss={() => setShowFileTreePanel(false)}
                                    showSeparator={true}
                                    titleProps={{
                                        size: TitleSize.Small,
                                        text: Resources.ChangedFilesPanelTitle
                                    }}
                                />
                                {explorerTree(true, changesContext)}
                            </CustomPanel>
                        </ConditionalChildren>
                        {changeViewer(true, changesContext)}
                    </ScreenSizeConditional>
                    <ScreenSizeConditional condition={screenSize => screenSize !== ScreenSize.xsmall}>
                        <Splitter
                            className="scroll-hidden bolt-page-grey repos-changes-explorer-splitter"
                            collapsed={isTreeCollapsed}
                            expandTooltip={Resources.ShowFilesTree}
                            farElementClassName="flex-column relative"
                            fixedElement={SplitterElementPosition.Near}
                            initialFixedSize={parseInt("" + tryGetLocalStorageValue(splitterSettingsKey)) || 300}
                            minFixedSize={180}
                            nearElementClassName="flex-column relative"
                            onFixedSizeChanged={newSize => {
                                trySetLocalStorageValue(splitterSettingsKey, "" + newSize);
                            }}
                            onCollapsedChanged={setTreeCollapsed}
                            onRenderFarElement={() => changeViewer(false, changesContext)}
                            onRenderNearElement={() => explorerTree(false, changesContext)}
                        />
                    </ScreenSizeConditional>
                </>
            )}
        </ChangesContext.Consumer>
    );
}
